import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './StudyLocations.css';

const StudyLocations = () => {
  const { orgDomain, isOrgAdminUser, isTeamMember } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
  });
  const [processing, setProcessing] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [campusSearch, setCampusSearch] = useState('');
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const searchBoxRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  // Study Goals state
  const [studyGoals, setStudyGoals] = useState({
    defaultGoal: { hours: '', period: 'week' },
    gpaRules: []
  });
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState({
    minGpa: '',
    maxGpa: '',
    hours: '',
    period: 'week'
  });


  const loadLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!orgDomain) return;

      const locationsRef = collection(db, 'organizations', orgDomain, 'studyLocations');
      const snapshot = await getDocs(locationsRef);
      
      const locationsData = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        locationsData.push({
          id: doc.id,
          name: data.name || '',
          address: data.address || '',
          latitude: data.latitude || 0,
          longitude: data.longitude || 0,
          createdAt: data.createdAt?.toDate() || new Date(),
        });
      });

      // Sort by creation date (newest first)
      locationsData.sort((a, b) => b.createdAt - a.createdAt);
      setLocations(locationsData);
    } catch (err) {
      console.error('Error loading study locations:', err);
      setError('Failed to load study locations. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgDomain]);

  const loadStudyGoals = useCallback(async () => {
    try {
      if (!orgDomain) return;

      const orgRef = doc(db, 'organizations', orgDomain);
      const orgDoc = await getDoc(orgRef);
      
      if (orgDoc.exists()) {
        const data = orgDoc.data();
        const goals = data.studyGoals || { defaultGoal: { hours: '', period: 'week' }, gpaRules: [] };
        setStudyGoals(goals);
      }
    } catch (err) {
      console.error('Error loading study goals:', err);
    }
  }, [orgDomain]);

  const saveStudyGoals = async (newGoals) => {
    try {
      setProcessing(true);
      const orgRef = doc(db, 'organizations', orgDomain);
      await updateDoc(orgRef, {
        studyGoals: newGoals
      });
      setStudyGoals(newGoals);
      alert('Study goals updated successfully!');
    } catch (err) {
      console.error('Error saving study goals:', err);
      alert('Failed to save study goals. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (orgDomain && (isOrgAdminUser || isTeamMember)) {
      loadLocations();
      loadStudyGoals();
    }
  }, [orgDomain, isOrgAdminUser, isTeamMember, loadLocations, loadStudyGoals]);

  // Load Google Maps API
  useEffect(() => {
    if (showAddModal && !mapLoaded) {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.error('Google Maps API key is not configured');
        return;
      }

      // Check if script is already loaded
      if (window.google && window.google.maps) {
        setMapLoaded(true);
        return;
      }

      // Load Google Maps script (without places library to avoid legacy API issues)
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        setMapLoaded(true);
      };
      document.head.appendChild(script);

      return () => {
        // Cleanup: remove script if component unmounts
        const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
        if (existingScript) {
          // Don't remove it as it might be used elsewhere
        }
      };
    }
  }, [showAddModal, mapLoaded]);

  // Initialize map when modal opens and API is loaded
  useEffect(() => {
    if (showAddModal && mapLoaded && window.google && window.google.maps && mapRef.current) {
      initializeMap();
    }

    return () => {
      // Cleanup map when modal closes
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
      if (autocompleteRef.current && autocompleteRef.current.cleanup) {
        autocompleteRef.current.cleanup();
        autocompleteRef.current = null;
      }
    };
  }, [showAddModal, mapLoaded]);

  const updateMapMarker = (lat, lng) => {
    if (!mapInstanceRef.current || !window.google) return;

    const position = { lat: parseFloat(lat), lng: parseFloat(lng) };
    
    if (isNaN(position.lat) || isNaN(position.lng)) return;

    // Center map on coordinates
    mapInstanceRef.current.setCenter(position);
    mapInstanceRef.current.setZoom(17);

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Create new marker
    markerRef.current = new window.google.maps.Marker({
      position: position,
      map: mapInstanceRef.current,
      draggable: true,
      title: 'Study Location',
    });

    // Make marker draggable
    markerRef.current.addListener('dragend', (e) => {
      const newLat = e.latLng.lat();
      const newLng = e.latLng.lng();
      setNewLocation((prev) => ({
        ...prev,
        latitude: newLat.toString(),
        longitude: newLng.toString(),
      }));

      // Reverse geocode new position
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setNewLocation((prev) => ({
            ...prev,
            address: results[0].formatted_address || prev.address,
          }));
        }
      });
    });
  };

  const initializeMap = () => {
    if (!window.google || !window.google.maps || !mapRef.current) return;

    // Use existing coordinates if available, otherwise default center
    let initialCenter = { lat: 40.7128, lng: -74.0060 }; // New York City as default
    let initialZoom = 15;

    if (newLocation.latitude && newLocation.longitude) {
      const lat = parseFloat(newLocation.latitude);
      const lng = parseFloat(newLocation.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        initialCenter = { lat, lng };
        initialZoom = 17;
      }
    }

    // Create map
    const map = new window.google.maps.Map(mapRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    // If coordinates exist, add marker
    if (newLocation.latitude && newLocation.longitude) {
      const lat = parseFloat(newLocation.latitude);
      const lng = parseFloat(newLocation.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        updateMapMarker(lat, lng);
      }
    }

    // Note: Search functionality is now handled by the React component's button and Enter key handler
    // No need to set up event listeners here since we're using React's controlled input

    // Add click listener to map
    map.addListener('click', (e) => {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // Remove existing marker
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }

      // Create new marker
      markerRef.current = new window.google.maps.Marker({
        position: { lat, lng },
        map: map,
        draggable: true,
        title: 'Study Location',
      });

      // Update form coordinates
      setNewLocation((prev) => ({
        ...prev,
        latitude: lat.toString(),
        longitude: lng.toString(),
      }));

      // Reverse geocode to get address
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          setNewLocation((prev) => ({
            ...prev,
            address: results[0].formatted_address || prev.address,
          }));
        }
      });

      // Make marker draggable
      markerRef.current.addListener('dragend', (e) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        setNewLocation((prev) => ({
          ...prev,
          latitude: newLat.toString(),
          longitude: newLng.toString(),
        }));

        // Reverse geocode new position
        geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
          if (status === 'OK' && results[0]) {
            setNewLocation((prev) => ({
              ...prev,
              address: results[0].formatted_address || prev.address,
            }));
          }
        });
      });
    });
  };

  const handleGeocodeAddress = async () => {
    if (!newLocation.address.trim()) {
      alert('Please enter an address');
      return;
    }

    try {
      setProcessing(true);
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      
      if (!apiKey) {
        alert('Google Maps API key is not configured. Please contact the administrator.');
        return;
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(newLocation.address)}&key=${apiKey}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        const location = result.geometry.location;
        const lat = location.lat.toString();
        const lng = location.lng.toString();
        
        setNewLocation({
          ...newLocation,
          latitude: lat,
          longitude: lng,
        });

        // Update map marker if map is loaded
        if (mapLoaded && mapInstanceRef.current) {
          updateMapMarker(lat, lng);
        }
      } else {
        alert('Could not find location for that address. Please enter coordinates manually.');
      }
    } catch (err) {
      console.error('Error geocoding address:', err);
      alert('Error geocoding address. Please enter coordinates manually.');
    } finally {
      setProcessing(false);
    }
  };

  // Update map marker when coordinates are manually entered
  useEffect(() => {
    if (mapLoaded && mapInstanceRef.current && newLocation.latitude && newLocation.longitude) {
      const lat = parseFloat(newLocation.latitude);
      const lng = parseFloat(newLocation.longitude);
      
      // Only update if coordinates are valid and different from current marker
      if (!isNaN(lat) && !isNaN(lng)) {
        if (!markerRef.current || 
            Math.abs(markerRef.current.getPosition().lat() - lat) > 0.0001 ||
            Math.abs(markerRef.current.getPosition().lng() - lng) > 0.0001) {
          updateMapMarker(lat, lng);
        }
      }
    }
  }, [newLocation.latitude, newLocation.longitude, mapLoaded]);

  const handleAddLocation = async () => {
    if (!newLocation.name.trim() || !newLocation.address.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const lat = parseFloat(newLocation.latitude);
    const lng = parseFloat(newLocation.longitude);

    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid latitude and longitude coordinates');
      return;
    }

    try {
      setProcessing(true);
      const locationsRef = collection(db, 'organizations', orgDomain, 'studyLocations');
      await addDoc(locationsRef, {
        name: newLocation.name.trim(),
        address: newLocation.address.trim(),
        latitude: lat,
        longitude: lng,
        organizationDomain: orgDomain,
        createdAt: new Date(),
      });

      setNewLocation({ name: '', address: '', latitude: '', longitude: '' });
      setCampusSearch('');
      setShowAddModal(false);
      setMapLoaded(false);
      await loadLocations();
    } catch (err) {
      console.error('Error adding study location:', err);
      alert('Failed to add study location. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewLocation({ name: '', address: '', latitude: '', longitude: '' });
    setCampusSearch('');
    setMapLoaded(false);
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this study location?')) {
      return;
    }

    try {
      setProcessing(true);
      await deleteDoc(doc(db, 'organizations', orgDomain, 'studyLocations', locationId));
      await loadLocations();
    } catch (err) {
      console.error('Error deleting study location:', err);
      alert('Failed to delete study location. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateDefaultGoal = async () => {
    const hours = parseFloat(studyGoals.defaultGoal.hours);
    if (studyGoals.defaultGoal.hours && (isNaN(hours) || hours <= 0)) {
      alert('Please enter a valid number of hours');
      return;
    }

    await saveStudyGoals(studyGoals);
  };

  const handleAddGpaRule = () => {
    const minGpa = parseFloat(editingGoal.minGpa);
    const maxGpa = parseFloat(editingGoal.maxGpa);
    const hours = parseFloat(editingGoal.hours);

    if (isNaN(minGpa) || isNaN(maxGpa) || isNaN(hours)) {
      alert('Please fill in all fields with valid numbers');
      return;
    }

    if (minGpa < 0 || minGpa > 4.0 || maxGpa < 0 || maxGpa > 4.0) {
      alert('GPA values must be between 0.0 and 4.0');
      return;
    }

    if (minGpa >= maxGpa) {
      alert('Minimum GPA must be less than Maximum GPA');
      return;
    }

    if (hours <= 0) {
      alert('Hours must be greater than 0');
      return;
    }

    const newRule = {
      id: Date.now().toString(),
      minGpa,
      maxGpa,
      hours,
      period: editingGoal.period
    };

    const updatedGoals = {
      ...studyGoals,
      gpaRules: [...studyGoals.gpaRules, newRule]
    };

    saveStudyGoals(updatedGoals);
    setEditingGoal({ minGpa: '', maxGpa: '', hours: '', period: 'week' });
    setShowGoalsModal(false);
  };

  const handleDeleteGpaRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this GPA rule?')) {
      return;
    }

    const updatedGoals = {
      ...studyGoals,
      gpaRules: studyGoals.gpaRules.filter(rule => rule.id !== ruleId)
    };

    await saveStudyGoals(updatedGoals);
  };

  // Check if user is admin or team member
  if (!isOrgAdminUser && !isTeamMember) {
    return (
      <div className="study-locations-page">
        <div className="page-header">
          <h1>Study Locations</h1>
        </div>
        <div className="error-state">
          <p>You must be an admin or team member to manage study locations.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="study-locations-page">
        <div className="page-header">
          <h1>Study Locations</h1>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading study locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="study-locations-page">
        <div className="page-header">
          <h1>Study Locations</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={loadLocations} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="study-locations-page">
      <div className="page-header">
        <h1>Study Locations</h1>
        {orgDomain && <p className="org-domain">Domain: {orgDomain}</p>}
        <button onClick={() => setShowAddModal(true)} className="add-button">
          + Add Location
        </button>
      </div>

      <div className="locations-info">
        <p>
          Students can track their study time when they are within 100 meters of a study location.
          Add locations like libraries, student centers, or study halls.
        </p>
      </div>

      {/* Study Goals Section */}
      <div className="study-goals-section">
        <div className="section-header">
          <h2>Study Hour Goals</h2>
          <button onClick={() => setShowGoalsModal(true)} className="add-goal-button">
            + Add GPA Rule
          </button>
        </div>
        
        <div className="goals-container">
          {/* Default Goal */}
          <div className="goal-card default-goal-card">
            <h3>Default Goal (All Students)</h3>
            <p className="goal-description">
              Set a default study hour requirement for all students who don't match any GPA rule.
            </p>
            <div className="goal-input-group">
              <input
                type="number"
                min="0"
                step="0.5"
                value={studyGoals.defaultGoal.hours}
                onChange={(e) => setStudyGoals({
                  ...studyGoals,
                  defaultGoal: { ...studyGoals.defaultGoal, hours: e.target.value }
                })}
                placeholder="e.g., 10"
                className="goal-input"
              />
              <span className="goal-unit">hours per</span>
              <select
                value={studyGoals.defaultGoal.period}
                onChange={(e) => setStudyGoals({
                  ...studyGoals,
                  defaultGoal: { ...studyGoals.defaultGoal, period: e.target.value }
                })}
                className="period-select"
              >
                <option value="week">week</option>
                <option value="month">month</option>
              </select>
              <button 
                onClick={handleUpdateDefaultGoal} 
                disabled={processing}
                className="save-goal-button"
              >
                {processing ? 'Saving...' : 'Save'}
              </button>
            </div>
            {studyGoals.defaultGoal.hours && (
              <p className="goal-preview">
                ✓ Default requirement: {studyGoals.defaultGoal.hours} hours per {studyGoals.defaultGoal.period}
              </p>
            )}
          </div>

          {/* GPA Rules */}
          {studyGoals.gpaRules.length > 0 && (
            <div className="gpa-rules-container">
              <h3>GPA-Based Rules</h3>
              <p className="rules-description">
                These rules override the default goal for students with specific GPA ranges.
              </p>
              <div className="rules-list">
                {studyGoals.gpaRules.map((rule) => (
                  <div key={rule.id} className="rule-card">
                    <div className="rule-info">
                      <div className="rule-gpa">
                        GPA: {rule.minGpa.toFixed(2)} - {rule.maxGpa.toFixed(2)}
                      </div>
                      <div className="rule-requirement">
                        {rule.hours} hours per {rule.period}
                      </div>
                    </div>
                    {isOrgAdminUser && (
                      <button
                        onClick={() => handleDeleteGpaRule(rule.id)}
                        disabled={processing}
                        className="delete-rule-button"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {locations.length === 0 ? (
        <div className="empty-state">
          <p>No study locations yet. Add your first location to get started.</p>
        </div>
      ) : (
        <div className="locations-list">
          {locations.map((location) => (
            <div key={location.id} className="location-card">
              <div className="location-info">
                <h3>{location.name}</h3>
                <p className="location-address">{location.address}</p>
                <p className="location-coords">
                  Coordinates: {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </p>
                <p className="location-date">
                  Added: {location.createdAt.toLocaleDateString()}
                </p>
              </div>
              <div className="location-actions">
                <a
                  href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="view-map-button"
                >
                  View on Map
                </a>
                {isOrgAdminUser && (
                  <button
                    onClick={() => handleDeleteLocation(location.id)}
                    disabled={processing}
                    className="delete-button"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Study Location</h2>
              <button
                className="close-button"
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {/* Map Section */}
              <div className="map-section">
                <div className="form-group">
                  <label>Search for Campus Address (Optional)</label>
                  <div className="address-input-group">
                    <input
                      id="campus-search-input"
                      type="text"
                      value={campusSearch}
                      onChange={(e) => setCampusSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && mapLoaded && mapInstanceRef.current) {
                          e.preventDefault();
                          const geocoder = new window.google.maps.Geocoder();
                          geocoder.geocode({ address: campusSearch }, (results, status) => {
                            if (status === 'OK' && results && results.length > 0) {
                              const result = results[0];
                              const location = result.geometry.location;
                              mapInstanceRef.current.setCenter(location);
                              mapInstanceRef.current.setZoom(17);
                            }
                          });
                        }
                      }}
                      placeholder="e.g., University of California, Berkeley"
                      className="campus-search-input"
                    />
                    <button
                      onClick={() => {
                        if (mapLoaded && mapInstanceRef.current && campusSearch.trim()) {
                          const geocoder = new window.google.maps.Geocoder();
                          geocoder.geocode({ address: campusSearch }, (results, status) => {
                            if (status === 'OK' && results && results.length > 0) {
                              const result = results[0];
                              const location = result.geometry.location;
                              mapInstanceRef.current.setCenter(location);
                              mapInstanceRef.current.setZoom(17);
                            } else {
                              alert('Could not find that location. Please try a different search term.');
                            }
                          });
                        }
                      }}
                      disabled={!mapLoaded || !campusSearch.trim()}
                      className="geocode-button"
                    >
                      Search
                    </button>
                  </div>
                  <p className="help-text">
                    Enter your campus address and click "Search" to center the map, then click on the map to drop a pin at the study location.
                  </p>
                </div>
                <div className="map-container">
                  {!mapLoaded && (
                    <div className="map-loading">
                      <div className="spinner"></div>
                      <p>Loading map...</p>
                    </div>
                  )}
                  <div ref={mapRef} className="google-map" style={{ display: mapLoaded ? 'block' : 'none' }}></div>
                </div>
                <p className="help-text map-instructions">
                  💡 Click anywhere on the map to place a pin. You can drag the pin to adjust its position.
                </p>
              </div>

              {/* Form Section */}
              <div className="form-section">
                <div className="form-group">
                  <label>Location Name *</label>
                  <input
                    type="text"
                    value={newLocation.name}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, name: e.target.value })
                    }
                    placeholder="e.g., Main Library"
                  />
                </div>
                <div className="form-group">
                  <label>Address *</label>
                  <div className="address-input-group">
                    <input
                      type="text"
                      value={newLocation.address}
                      onChange={(e) =>
                        setNewLocation({ ...newLocation, address: e.target.value })
                      }
                      placeholder="e.g., 123 University Ave, City, State"
                    />
                    <button
                      onClick={handleGeocodeAddress}
                      disabled={processing || !newLocation.address.trim()}
                      className="geocode-button"
                    >
                      {processing ? 'Geocoding...' : 'Get Coordinates'}
                    </button>
                  </div>
                  <p className="help-text">
                    The address will be automatically filled when you click on the map, or you can enter it manually.
                  </p>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Latitude *</label>
                    <input
                      type="number"
                      step="any"
                      value={newLocation.latitude}
                      onChange={(e) =>
                        setNewLocation({ ...newLocation, latitude: e.target.value })
                      }
                      placeholder="e.g., 40.7128"
                    />
                  </div>
                  <div className="form-group">
                    <label>Longitude *</label>
                    <input
                      type="number"
                      step="any"
                      value={newLocation.longitude}
                      onChange={(e) =>
                        setNewLocation({ ...newLocation, longitude: e.target.value })
                      }
                      placeholder="e.g., -74.0060"
                    />
                  </div>
                </div>
                <p className="help-text">
                  Coordinates will be automatically filled when you click on the map, or you can enter them manually.
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={handleCloseModal}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLocation}
                disabled={processing}
                className="save-button"
              >
                {processing ? 'Adding...' : 'Add Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add GPA Rule Modal */}
      {showGoalsModal && (
        <div className="modal-overlay" onClick={() => setShowGoalsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add GPA-Based Study Goal</h2>
              <button
                className="close-button"
                onClick={() => setShowGoalsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Create a study hour requirement for students within a specific GPA range.
                Students matching this rule will need to meet these hours instead of the default goal.
              </p>
              
              <div className="form-group">
                <label>Minimum GPA *</label>
                <input
                  type="number"
                  min="0"
                  max="4.0"
                  step="0.01"
                  value={editingGoal.minGpa}
                  onChange={(e) => setEditingGoal({ ...editingGoal, minGpa: e.target.value })}
                  placeholder="e.g., 0.0"
                />
              </div>

              <div className="form-group">
                <label>Maximum GPA *</label>
                <input
                  type="number"
                  min="0"
                  max="4.0"
                  step="0.01"
                  value={editingGoal.maxGpa}
                  onChange={(e) => setEditingGoal({ ...editingGoal, maxGpa: e.target.value })}
                  placeholder="e.g., 2.5"
                />
              </div>

              <div className="form-group">
                <label>Required Study Hours *</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={editingGoal.hours}
                  onChange={(e) => setEditingGoal({ ...editingGoal, hours: e.target.value })}
                  placeholder="e.g., 15"
                />
              </div>

              <div className="form-group">
                <label>Time Period *</label>
                <select
                  value={editingGoal.period}
                  onChange={(e) => setEditingGoal({ ...editingGoal, period: e.target.value })}
                >
                  <option value="week">Per Week</option>
                  <option value="month">Per Month</option>
                </select>
              </div>

              {editingGoal.minGpa && editingGoal.maxGpa && editingGoal.hours && (
                <div className="goal-preview-box">
                  <strong>Preview:</strong> Students with GPA between {editingGoal.minGpa} and {editingGoal.maxGpa} 
                  {' '}must complete {editingGoal.hours} hours per {editingGoal.period}.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowGoalsModal(false);
                  setEditingGoal({ minGpa: '', maxGpa: '', hours: '', period: 'week' });
                }}
                className="cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleAddGpaRule}
                disabled={processing}
                className="save-button"
              >
                {processing ? 'Adding...' : 'Add Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyLocations;
